"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Menu, Search, X } from "lucide-react";
import type { StaticNavLink } from "@/config/nav-links";
import { authLinks } from "@/config/nav-links";
import { cn } from "@/lib/utils";
import { navIcons } from "./nav-icons";
import NavMegaMenu from "./NavMegaMenu";
import MarketsMegaMenu from "./MarketsMegaMenu";
import { useNavThemeStyles } from "./nav-theme-context";
import { useNavProfile } from "./nav-profile";
import { NavLoginEvent, useNavLogin } from "@/app/hooks/use-nav-login";
import TrackedLink from "./TrackedLink";

type MobileNavMenuProps = {
    links: StaticNavLink[];
};

const MobileNavMenu = ({ links }: MobileNavMenuProps) => {
    const theme = useNavThemeStyles();
    const { isAuthenticated } = useNavProfile();
    const handleLogin = useNavLogin();
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState<StaticNavLink | null>(null);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!menuOpen) setActiveSection(null);
    }, [menuOpen]);

    const closeMenu = () => setMenuOpen(false);

    const handleBack = () => {
        if (activeSection) {
            setActiveSection(null);
            return;
        }
        closeMenu();
    };

    return (
        <>
            <button
                type="button"
                className={cn("sm:hidden", theme.link)}
                onClick={() => setMenuOpen(true)}
                aria-controls="mobile-nav-menu"
                aria-expanded={menuOpen}
            >
                <span className="sr-only">Open menu</span>
                <Menu />
            </button>

            {menuOpen && (
                <div
                    id="mobile-nav-menu"
                    className={cn(
                        "fixed inset-0 z-50 flex flex-col font-inter sm:hidden",
                        theme.mobileShell,
                    )}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    <div className="flex shrink-0 items-center justify-between px-4 pb-4 pt-3">
                        <button
                            type="button"
                            className={theme.link}
                            onClick={handleBack}
                            aria-label={activeSection ? "Back to menu" : "Close menu"}
                        >
                            <ChevronLeft className="size-6" strokeWidth={1.5} />
                        </button>
                        <div className="flex items-center gap-5">
                            <button
                                type="button"
                                className={theme.search}
                                aria-label="Search"
                            >
                                <Search className="size-6" strokeWidth={1.5} />
                            </button>
                            <button
                                type="button"
                                className={theme.link}
                                onClick={closeMenu}
                                aria-label="Close menu"
                            >
                                <X className="size-6" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>

                    <div
                        className={cn(
                            "flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl",
                            theme.mobileShell,
                        )}
                    >
                        {!activeSection ? (
                            <nav className="flex w-full flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4 pt-6">
                                {links.map((link) => {
                                    const Icon = link.icon ? navIcons[link.icon] : null;
                                    const itemClassName = cn(
                                        "flex w-full items-center gap-3 rounded-[var(--radius-m)] px-4 py-3 text-left text-xl font-regular leading-[26px] transition-colors",
                                        theme.mobileItem,
                                    );

                                    if (
                                        link.href &&
                                        !link.sections?.length &&
                                        !link.categories?.length
                                    ) {
                                        return (
                                            <TrackedLink
                                                key={link.label}
                                                href={link.href}
                                                event={link.eventName}
                                                cleverTapEvent={link.branchEvent}
                                                onClick={closeMenu}
                                                className={cn(itemClassName, "no-underline")}
                                            >
                                                {Icon && (
                                                    <Icon
                                                        className="size-6 shrink-0"
                                                        strokeWidth={1.5}
                                                        aria-hidden
                                                    />
                                                )}
                                                <span>{link.label}</span>
                                            </TrackedLink>
                                        );
                                    }

                                    return (
                                        <button
                                            key={link.label}
                                            type="button"
                                            className={itemClassName}
                                            onClick={() => setActiveSection(link)}
                                        >
                                            {Icon && (
                                                <Icon
                                                    className="size-6 shrink-0"
                                                    strokeWidth={1.5}
                                                    aria-hidden
                                                />
                                            )}
                                            <span>{link.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>
                        ) : (
                            <div className="flex-1 overflow-y-auto">
                                {activeSection.layout === "sidebar" &&
                                activeSection.categories ? (
                                    <MarketsMegaMenu
                                        sidebarTitle={activeSection.sidebarTitle}
                                        categories={activeSection.categories}
                                        onNavigate={closeMenu}
                                        variant="mobile"
                                    />
                                ) : (
                                    <NavMegaMenu
                                        sections={activeSection.sections}
                                        linkAsChild={false}
                                        onNavigate={closeMenu}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {!isAuthenticated && (
                        <div className="shrink-0 bg-white px-4 pb-6 pt-4">
                            <div className="flex w-full items-center justify-center gap-4">
                                {[...authLinks].reverse().map((link, index) => {
                                    const isPrimary = index === 0;
                                    const className = cn(
                                        "inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-m)] text-sm font-medium leading-5 no-underline",
                                        isPrimary
                                            ? "min-w-0 flex-1 bg-cta-primary px-6 py-2.5 text-cta-primary-text"
                                            : cn("shrink-0 px-4 py-2", theme.secondaryCta),
                                    );

                                    if (link.href === "/login") {
                                        return (
                                            <button
                                                key={link.label}
                                                type="button"
                                                className={className}
                                                onClick={() => {
                                                    closeMenu();
                                                    handleLogin(NavLoginEvent.LOGIN);
                                                }}
                                            >
                                                {link.label}
                                                <ChevronRight className="size-4 shrink-0" aria-hidden />
                                            </button>
                                        );
                                    }

                                    return (
                                        <TrackedLink
                                            key={link.href}
                                            href={link.href}
                                            event={link.eventName}
                                            target={link.openInNewTab ? "_blank" : undefined}
                                            rel={
                                                link.openInNewTab
                                                    ? "noopener noreferrer"
                                                    : undefined
                                            }
                                            onClick={closeMenu}
                                            className={className}
                                        >
                                            {link.label}
                                            <ChevronRight className="size-4 shrink-0" aria-hidden />
                                        </TrackedLink>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default MobileNavMenu;
